import type { DefaultOptionType } from 'antd/es/select'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react'
import type { PropsWithChildren } from 'react'

type LabelRegistry = Record<string, Record<string, string>>

type FilterLabelsContextValue = {
  registerOptions: (key: string, options?: DefaultOptionType[]) => void
  getLabel: (key: string, value: string | number) => string | undefined
}

const FilterLabelsContext = createContext<FilterLabelsContextValue | null>(null)

const flattenOptions = (options: DefaultOptionType[]): DefaultOptionType[] =>
  options.flatMap((option) =>
    option?.options?.length ? flattenOptions(option.options) : [option]
  )

export const FilterLabelsProvider = ({ children }: PropsWithChildren) => {
  const [registry, setRegistry] = useState<LabelRegistry>({})

  const registerOptions = useCallback(
    (key: string, options?: DefaultOptionType[]) => {
      if (!key || !options?.length) return

      setRegistry((prev) => {
        const current = prev[key] ?? {}
        const next = { ...current }

        for (const option of flattenOptions(options)) {
          if (option?.value == null) continue

          const value = String(option.value)
          const label =
            typeof option.label === 'string'
              ? option.label
              : typeof option.title === 'string'
                ? option.title
                : undefined

          if (label) next[value] = label
        }

        const currentKeys = Object.keys(current)
        const changed =
          Object.keys(next).length !== currentKeys.length ||
          currentKeys.some((value) => current[value] !== next[value])

        return changed ? { ...prev, [key]: next } : prev
      })
    },
    []
  )

  const getLabel = useCallback(
    (key: string, value: string | number) => registry[key]?.[String(value)],
    [registry]
  )

  const value = useMemo(
    () => ({ registerOptions, getLabel }),
    [registerOptions, getLabel]
  )

  return <FilterLabelsContext value={value}>{children}</FilterLabelsContext>
}

export const useFilterLabels = () => useContext(FilterLabelsContext)
